/**
 * WebSocket hook for real-time messaging
 *
 * Phase 3 Implementation: WebSocket support for instant message delivery
 *
 * Features:
 * - Auto-reconnection on disconnect
 * - Typing indicators
 * - Real-time message updates
 * - Browser notifications for new messages
 * - React Query cache synchronization
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { appConfig } from '@/types/env'
import type { Message } from '@/lib/api'

interface WebSocketMessage {
  type: string
  [key: string]: any
}

interface TypingUser {
  user_id: number
  username: string
  timestamp: number
}

export interface UseWebSocketReturn {
  isConnected: boolean
  sendMessage: (conversationId: number, content: string) => void
  markAsRead: (conversationId: number) => void
  sendTypingIndicator: (conversationId: number, isTyping: boolean) => void
  typingUsers: TypingUser[]
}

/**
 * Hook for WebSocket connection management and real-time messaging
 */
export function useWebSocket(): UseWebSocketReturn {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const ws = useRef<WebSocket | null>(null)
  const reconnectTimeout = useRef<NodeJS.Timeout>()
  const typingTimeout = useRef<Map<number, NodeJS.Timeout>>(new Map())
  const [isConnected, setIsConnected] = useState(false)
  const [typingUsers, setTypingUsers] = useState<Map<number, TypingUser>>(new Map())

  // Clean up old typing indicators (after 3 seconds of inactivity)
  useEffect(() => {
    const interval = setInterval(() => {
      setTypingUsers((prev) => {
        const now = Date.now()
        const updated = new Map(prev)
        let hasChanges = false

        for (const [userId, user] of updated) {
          if (now - user.timestamp > 3000) {
            updated.delete(userId)
            hasChanges = true
          }
        }

        return hasChanges ? updated : prev
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const handleNewMessage = useCallback(
    (message: Message) => {
      // Update React Query cache for messages
      queryClient.setQueryData<Message[]>(
        ['conversations', message.conversation_id, 'messages'],
        (oldMessages) => {
          if (!oldMessages) return [message]

          // Check if message already exists (by ID)
          const exists = oldMessages.some((m) => m.id === message.id)
          if (exists) return oldMessages

          // Add new message to the end
          return [...oldMessages, message]
        }
      )

      // Invalidate conversations list to update last message and unread counts
      queryClient.invalidateQueries({
        queryKey: ['conversations'],
      })

      // Show browser notification if not from current user
      if (message.sender_id !== user?.id) {
        // Check if notifications are enabled
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(`New message from ${message.sender_username}`, {
            body: message.content.substring(0, 100),
            icon: '/icon.png',
            tag: `message-${message.id}`, // Prevent duplicate notifications
          })
        }
      }
    },
    [user?.id, queryClient]
  )

  const handleTypingIndicator = useCallback((data: any) => {
    setTypingUsers((prev) => {
      const updated = new Map(prev)

      if (data.is_typing) {
        updated.set(data.user_id, {
          user_id: data.user_id,
          username: data.username,
          timestamp: Date.now(),
        })
      } else {
        updated.delete(data.user_id)
      }

      return updated
    })
  }, [])

  const handleMessagesRead = useCallback((data: any) => {
    // Update UI to show messages as read
    // This could update read receipts UI in the future
    console.log(`User ${data.user_id} read messages in conversation ${data.conversation_id}`)
  }, [])

  const connect = useCallback(() => {
    if (!user || ws.current?.readyState === WebSocket.OPEN) return

    // Use centralized WebSocket URL from app config
    // Automatically derived from EXPO_PUBLIC_API_URL or explicitly set via EXPO_PUBLIC_WS_URL
    const wsUrl = appConfig.WS_URL

    console.log('Connecting to WebSocket:', wsUrl)

    try {
      // Note: WebSocket doesn't support credentials option like fetch
      // The browser will send cookies if they're from the same origin
      // Since we're on different ports, we need to ensure cookies are set with SameSite=None
      ws.current = new WebSocket(wsUrl)

      ws.current.onopen = () => {
        console.log('WebSocket connected')
        setIsConnected(true)
        clearTimeout(reconnectTimeout.current)

        // Request notification permission if not granted
        if ('Notification' in window && Notification.permission === 'default') {
          Notification.requestPermission()
        }
      }

      ws.current.onmessage = (event) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data)

          switch (data.type) {
            case 'connection_established':
              console.log('Connected as:', data.username, 'Conversations:', data.conversations)
              break

            case 'new_message':
              handleNewMessage(data.message)
              break

            case 'message_sent':
              // Confirmation that our message was sent
              console.log('Message sent successfully:', data.message.id)
              break

            case 'user_typing':
              handleTypingIndicator(data)
              break

            case 'messages_read':
              handleMessagesRead(data)
              break

            case 'marked_as_read':
              console.log(
                `Marked ${data.marked_count} messages as read in conversation ${data.conversation_id}`
              )
              // Invalidate conversations to update unread counts
              queryClient.invalidateQueries({ queryKey: ['conversations'] })
              break

            case 'error':
              console.error('WebSocket error:', data.error)
              break

            default:
              console.log('Unknown WebSocket message type:', data.type)
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error)
      }

      ws.current.onclose = (event) => {
        console.log('WebSocket disconnected. Code:', event.code, 'Reason:', event.reason)
        setIsConnected(false)

        // Reconnect after 3 seconds (unless it was a normal closure)
        if (event.code !== 1000) {
          reconnectTimeout.current = setTimeout(() => {
            console.log('Attempting to reconnect...')
            connect()
          }, 3000)
        }
      }
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error)
      setIsConnected(false)
      // Try to reconnect after 3 seconds
      reconnectTimeout.current = setTimeout(() => {
        console.log('Attempting to reconnect after error...')
        connect()
      }, 3000)
    }
  }, [user, handleNewMessage, handleTypingIndicator, handleMessagesRead, queryClient])

  const sendMessage = useCallback((conversationId: number, content: string) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(
        JSON.stringify({
          type: 'send_message',
          conversation_id: conversationId,
          content,
        })
      )
    } else {
      console.warn('WebSocket not connected. Message not sent.')
    }
  }, [])

  const markAsRead = useCallback((conversationId: number) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(
        JSON.stringify({
          type: 'mark_as_read',
          conversation_id: conversationId,
        })
      )
    }
  }, [])

  const sendTypingIndicator = useCallback((conversationId: number, isTyping: boolean) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      // Clear existing timeout for this conversation
      const existingTimeout = typingTimeout.current.get(conversationId)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
        typingTimeout.current.delete(conversationId)
      }

      // Send typing indicator
      ws.current.send(
        JSON.stringify({
          type: isTyping ? 'typing_start' : 'typing_stop',
          conversation_id: conversationId,
        })
      )

      // If starting to type, set timeout to automatically stop after 2 seconds
      if (isTyping) {
        const timeout = setTimeout(() => {
          sendTypingIndicator(conversationId, false)
        }, 2000)
        typingTimeout.current.set(conversationId, timeout)
      }
    }
  }, [])

  // Connect on mount and reconnect on user change
  useEffect(() => {
    // Only connect if there's a user
    if (user) {
      connect()
    }

    return () => {
      // Clean up on unmount
      clearTimeout(reconnectTimeout.current)
      typingTimeout.current.forEach((timeout) => clearTimeout(timeout))
      ws.current?.close(1000, 'Component unmounting')
    }
  }, [user, connect])

  return {
    isConnected,
    sendMessage,
    markAsRead,
    sendTypingIndicator,
    typingUsers: Array.from(typingUsers.values()),
  }
}
